/**
 * SocketContext — singleton WebSocket connection shared across the entire app.
 *
 * Features:
 *  - One WS connection per authenticated session (no duplicate connections)
 *  - Exponential-backoff auto-reconnect on close
 *  - Reconnects when the tab becomes visible again (visibilitychange)
 *  - Reconnects immediately when network comes back online (window 'online' event)
 *  - Fires toast notifications for new_message / ad_approved events
 *  - Keeps a local notificationCount that consumers (Header) can read
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { createElement } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { MessageCircle, CheckCircle, ShoppingBag, XCircle, Truck, Package, Bell, Scale } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { buildWsUrl, getAuthToken } from "@/lib/apiBase";

// ─── Types ────────────────────────────────────────────────────────────────────

type WsEvent =
  | { type: "connected" }
  | {
      type: string;
      conversationId?: number;
      content?: string;
      messageId?: number;
      title?: string;
      message?: string;
      orderId?: number;
      listingId?: number;
      status?: string;
      orderCode?: string;
    };

interface SocketContextValue {
  /** Whether the WebSocket is currently OPEN */
  isConnected: boolean;
  /** Count of NOTIFICATION events received since last clearNotifications() */
  notificationCount: number;
  /** Reset notificationCount to 0 — call when user opens the messages page */
  clearNotifications: () => void;
  /** Last conversationId received from a new_message WS event */
  lastNotifConvId: number | null;
  /** Clear the stored conversationId after navigation */
  clearLastNotifConvId: () => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const SocketContext = createContext<SocketContextValue>({
  isConnected: false,
  notificationCount: 0,
  clearNotifications: () => {},
  lastNotifConvId: null,
  clearLastNotifConvId: () => {},
});

// ─── Constants ────────────────────────────────────────────────────────────────

const BASE_DELAY_MS = 2_000;
const MAX_DELAY_MS = 30_000;

// ─── Provider ─────────────────────────────────────────────────────────────────

export function SocketProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [isConnected, setIsConnected] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [lastNotifConvId, setLastNotifConvId] = useState<number | null>(null);

  const clearNotifications = useCallback(() => setNotificationCount(0), []);
  const clearLastNotifConvId = useCallback(() => setLastNotifConvId(null), []);

  // Stable refs so inner functions always read the latest values
  const wsRef = useRef<WebSocket | null>(null);
  const toastRef = useRef(toast);
  const queryClientRef = useRef(queryClient);
  useEffect(() => { toastRef.current = toast; }, [toast]);
  useEffect(() => { queryClientRef.current = queryClient; }, [queryClient]);

  useEffect(() => {
    if (!isAuthenticated) {
      wsRef.current?.close();
      setIsConnected(false);
      return;
    }

    let cancelled = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let delay = BASE_DELAY_MS;

    // ── Notification sound ───────────────────────────────────────────────────
    function playSound() {
      try {
        const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioCtx) return;
        const ctx = new AudioCtx() as AudioContext;
        const doPlay = () => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.frequency.setValueAtTime(880, ctx.currentTime);
          osc.frequency.setValueAtTime(660, ctx.currentTime + 0.1);
          gain.gain.setValueAtTime(0.2, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
          osc.start();
          osc.stop(ctx.currentTime + 0.35);
        };
        // iOS WKWebView: AudioContext starts "suspended" until a user gesture
        // has been made. Resume it first, then play.
        if (ctx.state === "suspended") {
          ctx.resume().then(doPlay).catch(() => {});
        } else {
          doPlay();
        }
      } catch {}
    }

    function invalidateNotificationCaches() {
      queryClientRef.current.invalidateQueries({ queryKey: ["/api/merchant/notifications"] });
      queryClientRef.current.invalidateQueries({ queryKey: ["/api/merchant/notifications/unread-count"] });
      queryClientRef.current.invalidateQueries({ queryKey: ["/api/virtual-shop/notifications"] });
      queryClientRef.current.invalidateQueries({ queryKey: ["/api/virtual-shop/notifications/unread-count"] });
      queryClientRef.current.invalidateQueries({ queryKey: ["/api/buyer/notifications/unread-count"] });
      queryClientRef.current.invalidateQueries({ queryKey: ["/api/disputes/notifications/unread-count"] });
      // Unified in-app inbox (the header bell) — refresh badge + open list.
      queryClientRef.current.invalidateQueries({ queryKey: ["/api/notifications/inbox"] });
      queryClientRef.current.invalidateQueries({ queryKey: ["/api/notifications/inbox/unread-count"] });
    }

    // ── Core connect function ─────────────────────────────────────────────────
    function connect() {
      if (cancelled) return;

      // Reuse existing live connection
      const current = wsRef.current;
      if (current?.readyState === WebSocket.OPEN || current?.readyState === WebSocket.CONNECTING) {
        return;
      }

      // Append ?token=... ONLY when a token is stored (native shell). On the
      // web `getAuthToken()` returns null → URL is identical to before, and
      // the server falls through to its existing cookie-session handshake.
      const baseWsUrl = buildWsUrl("/ws");
      const token = getAuthToken();
      const wsUrl = token
        ? `${baseWsUrl}${baseWsUrl.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}`
        : baseWsUrl;
      console.log("[WS] connecting, token:", token ? "present" : "missing", "url-prefix:", baseWsUrl.split("?")[0]);
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled) { ws.close(); return; }
        console.log("[WS] connected");
        setIsConnected(true);
        delay = BASE_DELAY_MS; // reset backoff
      };

      ws.onmessage = (event: MessageEvent) => {
        if (cancelled) return;
        try {
          const data = JSON.parse(event.data as string) as WsEvent;
          console.log("[WS] message:", data.type);
          let handledToast = false;

          // ── new_message ────────────────────────────────────────────────────
          if (data.type === "new_message") {
            setNotificationCount((n) => n + 1);
            if (data.conversationId) setLastNotifConvId(data.conversationId);
            invalidateNotificationCaches();
            const messageContent = typeof data.content === "string" ? data.content : "";

            // Instant cache refresh — no need to wait 10s for next poll
            queryClientRef.current.invalidateQueries({
              queryKey: ["/api/messages/unread-count"],
            });
            // Fix: correct path is /api/conversations not /api/messages/conversations
            queryClientRef.current.invalidateQueries({
              queryKey: ["/api/conversations"],
            });

            const preview =
              messageContent.length > 90
                ? messageContent.slice(0, 90) + "…"
                : messageContent;

            console.log("[WS] showing toast for new_message");
            toastRef.current({
              title: createElement(
                "div",
                { className: "flex items-center gap-2" },
                createElement(MessageCircle, {
                  className: "h-4 w-4 text-blue-500 shrink-0",
                }),
                createElement("span", null, "رسالة جديدة")
              ) as unknown as ReactNode,
              description: createElement(
                "div",
                { className: "space-y-2" },
                createElement("p", { className: "text-sm" }, preview),
                createElement(
                  "button",
                  {
                    onClick: () => { window.location.href = `/messages${data.conversationId ? `?conv=${data.conversationId}` : ""}`; },
                    className: "text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 mt-1",
                  },
                  "→ فتح الرسائل"
                )
              ),
              duration: 10_000,
            } as any);
            handledToast = true;

            playSound();
          }

          if (data.type === "dispute_updated" || data.type === "dispute_opened") {
            invalidateNotificationCaches();
            queryClientRef.current.invalidateQueries({ queryKey: ["/api/disputes/my"] });
            const dTitle = typeof data.title === "string" ? data.title : "نزاع";
            const dBody = typeof data.message === "string" ? data.message : "";
            toastRef.current({
              title: createElement(
                "div",
                { className: "flex items-center gap-2" },
                createElement(Scale, { className: "h-4 w-4 text-red-500 shrink-0" }),
                createElement("span", null, dTitle),
              ) as unknown as ReactNode,
              description: dBody || undefined,
              duration: 12_000,
            } as any);
            playSound();
            handledToast = true;
          }

          // ── ad_approved ────────────────────────────────────────────────────
          if (data.type === "ad_approved") {
            invalidateNotificationCaches();
            toastRef.current({
              title: createElement(
                "div",
                { className: "flex items-center gap-2" },
                createElement(CheckCircle, {
                  className: "h-4 w-4 text-green-500 shrink-0",
                }),
                createElement("span", null, "تم قبول إعلانك!")
              ) as unknown as ReactNode,
              description: `إعلانك "${data.title}" أصبح نشطاً الآن`,
              duration: 10_000,
            } as any);
            handledToast = true;
            playSound();
          }

          // ── delivery_failed ───────────────────────────────────────────
          if (data.type === "delivery_failed") {
            invalidateNotificationCaches();
            queryClientRef.current.invalidateQueries({ queryKey: ["/api/orders"] });
            queryClientRef.current.invalidateQueries({ queryKey: ["/api/buyer/my-dropshipping-orders"] });
            queryClientRef.current.invalidateQueries({ queryKey: ["/api/buyer/own-product-orders"] });
            queryClientRef.current.invalidateQueries({ queryKey: ["/api/virtual-shop/orders"] });
            const dfTitle = typeof data.title === "string" ? data.title : "فشل توصيل طلبك";
            const dfBody = typeof data.message === "string" ? data.message : "";
            toastRef.current({
              title: createElement(
                "div",
                { className: "flex items-center gap-2" },
                createElement(XCircle, { className: "h-4 w-4 text-red-500 shrink-0" }),
                createElement("span", null, dfTitle),
              ) as unknown as ReactNode,
              description: dfBody || undefined,
              duration: 12_000,
            } as any);
            playSound();
            handledToast = true;
          }

          // ── order_update ────────────────────────────────────────────────
          if (data.type === "order_update") {
            invalidateNotificationCaches();
            const isRejected  = data.status === "rejected" || data.status === "cancelled";
            const isShipped   = data.status === "shipped";
            const isDelivered = data.status === "delivered";
            const iconEl = isRejected
              ? createElement(XCircle, { className: "h-4 w-4 text-red-500 shrink-0" })
              : isShipped
              ? createElement(Truck, { className: "h-4 w-4 text-blue-500 shrink-0" })
              : isDelivered
              ? createElement(CheckCircle, { className: "h-4 w-4 text-green-500 shrink-0" })
              : createElement(Package, { className: "h-4 w-4 text-orange-500 shrink-0" });

            console.log("[WS] showing toast for order_update status:", data.status);
            toastRef.current({
              title: createElement(
                "div",
                { className: "flex items-center gap-2" },
                iconEl,
                createElement("span", null, data.title)
              ) as unknown as ReactNode,
              description: data.message,
              duration: 10_000,
            } as any);
            handledToast = true;

            playSound();

            // Seller / shop / merchant side
            queryClientRef.current.invalidateQueries({ queryKey: ["/api/virtual-shop/orders"] });
            queryClientRef.current.invalidateQueries({ queryKey: ["/api/merchant/orders"] });
            queryClientRef.current.invalidateQueries({ queryKey: ["/api/dropshipping/orders", data.orderId] });
            queryClientRef.current.invalidateQueries({ queryKey: ["/api/store/own-product-orders"] });
            // Buyer side — refresh all three order lists so badge and page update immediately
            queryClientRef.current.invalidateQueries({ queryKey: ["/api/orders"] });
            queryClientRef.current.invalidateQueries({ queryKey: ["/api/buyer/my-dropshipping-orders"] });
            queryClientRef.current.invalidateQueries({ queryKey: ["/api/buyer/own-product-orders"] });
          }

          // ── delivery_otp ────────────────────────────────────────────────
          if (data.type === "delivery_otp") {
            invalidateNotificationCaches();
            const otpCode = typeof (data as any).otp === "string" ? (data as any).otp : "";
            const otpTitle = typeof data.title === "string" ? data.title : "رمز استلام طلبك";
            const otpMsg   = typeof data.message === "string" ? data.message : "";
            toastRef.current({
              title: createElement(
                "div",
                { className: "flex items-center gap-2" },
                createElement(Package, { className: "h-4 w-4 text-green-500 shrink-0" }),
                createElement("span", null, otpTitle),
              ) as unknown as ReactNode,
              description: otpCode
                ? createElement(
                    "div",
                    { className: "space-y-1" },
                    createElement("p", { className: "text-sm" }, otpMsg),
                    createElement(
                      "p",
                      { className: "text-lg font-bold tracking-widest text-green-600 dark:text-green-400" },
                      otpCode
                    )
                  )
                : otpMsg || undefined,
              duration: 30_000,
            } as any);
            playSound();
            queryClientRef.current.invalidateQueries({ queryKey: ["/api/orders"] });
            queryClientRef.current.invalidateQueries({ queryKey: ["/api/buyer/my-dropshipping-orders"] });
            queryClientRef.current.invalidateQueries({ queryKey: ["/api/buyer/own-product-orders"] });
            queryClientRef.current.invalidateQueries({ queryKey: ["/api/virtual-shop/orders"] });
            handledToast = true;
          }

          if (data.type === "new_cash_order") {
            invalidateNotificationCaches();
            queryClientRef.current.invalidateQueries({ queryKey: ["/api/merchant/cash-orders"] });

            toastRef.current({
              title: createElement(
                "div",
                { className: "flex items-center gap-2" },
                createElement(ShoppingBag, { className: "h-4 w-4 text-orange-500 shrink-0" }),
                createElement("span", null, "طلب نقد جديد 💰")
              ) as unknown as ReactNode,
              description: data.message,
              duration: 10_000,
            } as any);
            handledToast = true;

            playSound();
          }

          if (data.type === "new_direct_wholesale_order") {
            invalidateNotificationCaches();
            queryClientRef.current.invalidateQueries({
              queryKey: ["/api/wholesaler/direct-wholesale-orders"],
            });
            const body = typeof data.message === "string" ? data.message : "";
            toastRef.current({
              title: createElement(
                "div",
                { className: "flex items-center gap-2" },
                createElement(ShoppingBag, {
                  className: "h-4 w-4 text-amber-600 shrink-0",
                }),
                createElement("span", null, data.title || "طلب جملة مباشر جديد")
              ) as unknown as ReactNode,
              description: createElement(
                "div",
                { className: "space-y-2" },
                createElement("p", { className: "text-sm" }, body),
                createElement(
                  "button",
                  {
                    type: "button",
                    onClick: () => {
                      window.location.href = "/merchant/direct-wholesale-orders";
                    },
                    className:
                      "text-xs font-semibold text-amber-700 dark:text-amber-400 hover:underline flex items-center gap-1 mt-1",
                  },
                  "→ الطلبات المباشرة"
                )
              ),
              duration: 10_000,
            } as any);
            handledToast = true;
            playSound();
          }

          // ── new_order (dropshipping) / new_store_own_order (store-own) ───
          // A new order just landed for the virtual store. Refresh the store
          // order lists so it appears immediately — without this the 5-min
          // staleTime + no-refetch-on-focus left the «طلبات متجري» list showing
          // the stale (empty) result, so only the notification arrived.
          if (data.type === "new_order" || data.type === "new_store_own_order") {
            invalidateNotificationCaches();
            queryClientRef.current.invalidateQueries({ queryKey: ["/api/virtual-shop/orders"] });
            queryClientRef.current.invalidateQueries({ queryKey: ["/api/store/own-product-orders"] });
            const body = typeof data.message === "string" ? data.message : "";
            toastRef.current({
              title: createElement(
                "div",
                { className: "flex items-center gap-2" },
                createElement(ShoppingBag, { className: "h-4 w-4 text-amber-600 shrink-0" }),
                createElement("span", null, data.title || "وصلك طلب جديد 🎉"),
              ) as unknown as ReactNode,
              description: createElement(
                "div",
                { className: "space-y-2" },
                createElement("p", { className: "text-sm" }, body),
                createElement(
                  "button",
                  {
                    type: "button",
                    onClick: () => { window.location.href = "/virtual-shop/orders"; },
                    className:
                      "text-xs font-semibold text-amber-700 dark:text-amber-400 hover:underline flex items-center gap-1 mt-1",
                  },
                  "→ طلبات متجري",
                ),
              ),
              duration: 10_000,
            } as any);
            handledToast = true;
            playSound();
          }

          const fallbackTitle = "title" in data ? data.title : undefined;
          const fallbackBody = "message" in data ? (data.message || data.content) : undefined;
          if (data.type === "new_return") {
            invalidateNotificationCaches();
            queryClientRef.current.invalidateQueries({ queryKey: ["/api/returns/incoming"] });
            const rTitle = typeof data.title === "string" ? data.title : "طلب إرجاع جديد";
            const rBody = typeof data.message === "string" ? data.message : "";
            toastRef.current({
              title: createElement(
                "div",
                { className: "flex items-center gap-2" },
                createElement(Package, { className: "h-4 w-4 text-orange-500 shrink-0" }),
                createElement("span", null, rTitle),
              ) as unknown as ReactNode,
              description: rBody || undefined,
              duration: 10_000,
            } as any);
            playSound();
            handledToast = true;
          }

          // ── return_decision (buyer receives) ───────────────────────────
          if (data.type === "return_decision") {
            invalidateNotificationCaches();
            queryClientRef.current.invalidateQueries({ queryKey: ["/api/returns/mine"] });
            const rdTitle = typeof data.title === "string" ? data.title : "تحديث طلب الإرجاع";
            const rdBody = typeof data.message === "string" ? data.message : "";
            toastRef.current({
              title: createElement(
                "div",
                { className: "flex items-center gap-2" },
                createElement(CheckCircle, { className: "h-4 w-4 text-green-500 shrink-0" }),
                createElement("span", null, rdTitle),
              ) as unknown as ReactNode,
              description: rdBody || undefined,
              duration: 10_000,
            } as any);
            playSound();
            handledToast = true;
          }

          // Catch-all: any unhandled event with at least a title or message
          // still surfaces a toast + sound + cache invalidation, so we never
          // silently drop a server-emitted notification.
          if (!handledToast && (fallbackTitle || fallbackBody)) {
            invalidateNotificationCaches();
            toastRef.current({
              title: createElement(
                "div",
                { className: "flex items-center gap-2" },
                createElement(Bell, {
                  className: "h-4 w-4 text-purple-500 shrink-0",
                }),
                createElement("span", null, fallbackTitle || "تنبيه")
              ) as unknown as ReactNode,
              description: fallbackBody || undefined,
              duration: 10_000,
            } as any);
            playSound();
          }
        } catch (err) {
          console.error("[WS] onmessage error:", err);
          // Ignore malformed frames
        }
      };

      ws.onclose = (event: CloseEvent) => {
        console.log("[WS] closed, code:", event.code, "reason:", event.reason);
        setIsConnected(false);
        if (cancelled) return;

        if (event.code === 4001) {
          // Unauthorized — the Bearer token may not be stored yet (token-refresh
          // useEffect in use-auth.ts is async). Poll every 300ms for up to 6s so
          // we reconnect the instant the token lands, rather than always waiting
          // a fixed 1.5s blind delay.
          let pollsLeft = 20; // 20 × 300ms = 6s max
          const pollForToken = () => {
            if (cancelled) return;
            if (getAuthToken() || pollsLeft <= 0) {
              connect();
            } else {
              pollsLeft--;
              reconnectTimer = setTimeout(pollForToken, 300);
            }
          };
          reconnectTimer = setTimeout(pollForToken, 300);
          return;
        }

        // Normal exponential-backoff reconnect
        reconnectTimer = setTimeout(() => {
          delay = Math.min(delay * 2, MAX_DELAY_MS);
          connect();
        }, delay);
      };

      ws.onerror = () => ws.close();
    }

    connect();

    // ── Mobile / network reconnection triggers ────────────────────────────────
    function handleVisibilityChange() {
      // Re-establish when the tab/app comes back into focus
      if (!document.hidden) connect();
    }

    function handleOnline() {
      // Network restored — reset backoff and reconnect immediately
      delay = BASE_DELAY_MS;
      connect();
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("online", handleOnline);

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      wsRef.current?.close();
      wsRef.current = null;
      setIsConnected(false);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);
    };
  }, [isAuthenticated]); // Re-run only when auth state changes

  return (
    <SocketContext.Provider value={{ isConnected, notificationCount, clearNotifications, lastNotifConvId, clearLastNotifConvId }}>
      {children}
    </SocketContext.Provider>
  );
}

// ─── Consumer hook ────────────────────────────────────────────────────────────

/**
 * useSocket — access the real-time WebSocket state from any component.
 *
 * @example
 *   const { isConnected, notificationCount, clearNotifications } = useSocket();
 */
export function useSocket() {
  return useContext(SocketContext);
}
